import { useState, useRef, useEffect } from 'react';
import { default as api } from '../api';
import { useT, useI18n } from '../i18n';
import { MessageCircleQuestion, X, Send, Loader2, Headset } from 'lucide-react';

/**
 * 全局支持浮窗 (W7b) — 帮助 AI (KB 限定) + 一键转人工工单。
 * 登录用户 50 轮/日; 访客见落地页 vanilla 版。
 */
export default function SupportWidget({ variant = 'floating', open: openProp, onClose } = {}) {
  const t = useT();
  const { lang } = useI18n();
  const [open, setOpen] = useState(false);
  const isOpen = variant === 'inline' ? !!openProp : open;
  const toggle = () => { if (variant === 'inline') { onClose && onClose(); } else { setOpen(o => !o); } };
  const [tab, setTab] = useState('chat'); // chat | ticket
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [suggestTicket, setSuggestTicket] = useState(false);
  const [subject, setSubject] = useState('');
  const [ticketMsg, setTicketMsg] = useState('');
  const [ticketDone, setTicketDone] = useState(false);
  const [ticketBusy, setTicketBusy] = useState(false);
  const bottomRef = useRef(null);

  // 全局打开入口 (如首次登录引导页帮助链接) — 派发 'oz:open-support' 事件
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('oz:open-support', handler);
    return () => window.removeEventListener('oz:open-support', handler);
  }, []);

  // 2026-08-25（工单语音，仅家庭版语音会话可触发）：prefill 代填 + operate 确认门控代操作。
  // 组件常驻挂载（Layout），事件无会话时永不派发，商务版/手动使用零影响。
  useEffect(() => {
    const ack = (callId, ok, reason) => {
      if (!callId) return;
      window.dispatchEvent(new CustomEvent('voice:prefill-result', { detail: { callId, ok, reason: reason || null } }));
    };
    const onPrefill = (e) => {
      const d = e.detail;
      if (!d || typeof d.text !== 'string') return;
      if (d.field === 'support.input') {
        if (variant === 'floating') setOpen(true);
        setTab('chat');
        setInput(d.text);
        ack(d.callId, true);
      } else if (d.field === 'support.subject') {
        if (variant === 'floating') setOpen(true);
        setTab('ticket');
        setSubject(d.text);
        ack(d.callId, true);
      } else if (d.field === 'support.message') {
        if (variant === 'floating') setOpen(true);
        setTab('ticket');
        setTicketMsg(d.text);
        ack(d.callId, true);
      }
    };
    const onOperate = (e) => {
      const op = e.detail && e.detail.op;
      // 守卫与按钮 disabled 同构：空草稿/长度不足/进行中 → 静默不执行（不伪报）
      if (op === 'support.send') {
        if (input.trim() && !sending) send();
      } else if (op === 'support.submit') {
        if (subject.trim().length >= 4 && ticketMsg.trim().length >= 10 && !ticketBusy) submitTicket();
      }
    };
    window.addEventListener('voice:prefill', onPrefill);
    window.addEventListener('voice:operate', onOperate);
    return () => {
      window.removeEventListener('voice:prefill', onPrefill);
      window.removeEventListener('voice:operate', onOperate);
    };
  }, [variant, input, sending, subject, ticketMsg, ticketBusy]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, open]);

  const authHeader = () => {
    const token = sessionStorage.getItem('socioniq_token');
    return token ? { Authorization: 'Bearer ' + token } : {};
  };

  // P1-3: 函数声明(提升) — 修复 no-use-before-define (onOperate 事件回调内引用)
  async function send() {
    const text = input.trim();
    if (!text || sending) return;
    const history = messages.slice(-8).map(m => ({ role: m.role, content: m.content }));
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInput('');
    setSending(true);
    try {
      const res = await api.post('/support/chat',
        { message: text, history, ui_language: lang, page: window.location.pathname },
        { headers: authHeader() });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.reply }]);
      setSuggestTicket(!!res.data.suggest_ticket);
    } catch (err) {
      const d = err.response?.data;
      setMessages(prev => [...prev, { role: 'assistant', content: d?.message || t('support.error') }]);
      if (d?.suggest_ticket) setSuggestTicket(true);
    } finally {
      setSending(false);
    }
  }

  // P1-3: 函数声明(提升) — 修复 no-use-before-define (onOperate 事件回调内引用)
  async function submitTicket() {
    if (!subject.trim() || !ticketMsg.trim() || ticketBusy) return;
    setTicketBusy(true);
    try {
      await api.post('/support/ticket',
        { subject: subject.trim(), message: ticketMsg.trim() },
        { headers: authHeader() });
      setTicketDone(true);
    } catch (err) {
      alert(err.response?.data?.message || t('support.error'));
    } finally {
      setTicketBusy(false);
    }
  }

  return (
    <>
      {variant === 'floating' && (
        <button
          onClick={toggle}
          data-voice-anchor="support.open"
          className="fixed bottom-5 right-5 z-50 w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-105"
          style={{ backgroundColor: 'var(--brand)', color: '#fff' }}
          title={t('support.title')}>
          {open ? <X size={22} /> : <MessageCircleQuestion size={22} />}
        </button>
      )}

      {isOpen && (
        <div className={variant === 'floating'
          ? 'fixed bottom-20 right-5 z-50 w-[340px] max-h-[480px] rounded-2xl shadow-2xl flex flex-col overflow-hidden'
          : 'absolute top-16 right-4 z-40 w-[340px] max-h-[480px] rounded-2xl shadow-2xl flex flex-col overflow-hidden'}
          style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border)' }}>
          <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: '1px solid var(--border)' }}>
            <Headset size={16} style={{ color: 'var(--brand)' }} />
            <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{t('support.title')}</span>
            <div className="ml-auto flex gap-1">
              {variant === 'inline' && (
                <button onClick={toggle} title={t('support.close')}
                  className="w-6 h-6 rounded-lg flex items-center justify-center hover:opacity-70"
                  style={{ color: 'var(--text-muted)' }}>
                  <X size={14} />
                </button>
              )}
              <button onClick={() => setTab('chat')}
                className="px-2.5 py-1 rounded-lg text-xs"
                style={{ backgroundColor: tab === 'chat' ? 'var(--brand)' : 'var(--bg-hover)', color: tab === 'chat' ? '#fff' : 'var(--text-secondary)' }}>
                {t('support.tab_chat')}
              </button>
              <button onClick={() => setTab('ticket')}
                className="px-2.5 py-1 rounded-lg text-xs"
                style={{ backgroundColor: tab === 'ticket' ? 'var(--brand)' : 'var(--bg-hover)', color: tab === 'ticket' ? '#fff' : 'var(--text-secondary)' }}>
                {t('support.tab_ticket')}
              </button>
            </div>
          </div>

          {tab === 'chat' && (
            <>
              <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-[200px] max-h-[320px]">
                {messages.length === 0 && (
                  <p className="text-xs text-center py-6" style={{ color: 'var(--text-muted)' }}>{t('support.welcome')}</p>
                )}
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className="max-w-[85%] px-3 py-2 rounded-xl text-xs leading-relaxed whitespace-pre-wrap"
                      style={{
                        backgroundColor: m.role === 'user' ? 'var(--brand)' : 'var(--bg-hover)',
                        color: m.role === 'user' ? '#fff' : 'var(--text-primary)',
                      }}>
                      {m.content}
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex justify-start">
                    <div className="px-3 py-2 rounded-xl" style={{ backgroundColor: 'var(--bg-hover)' }}>
                      <Loader2 size={14} className="animate-spin" style={{ color: 'var(--text-muted)' }} />
                    </div>
                  </div>
                )}
                <div ref={bottomRef} />
              </div>
              {suggestTicket && (
                <button onClick={() => { setTab('ticket'); setTicketMsg(messages.filter(m => m.role === 'user').map(m => m.content).join('\n').slice(0, 500)); }}
                  className="mx-3 mb-2 px-3 py-1.5 rounded-lg text-xs font-medium"
                  style={{ backgroundColor: 'rgba(234,179,8,0.15)', color: 'rgb(202,138,4)' }}>
                  {t('support.to_ticket')}
                </button>
              )}
              <div className="flex items-center gap-2 p-3" style={{ borderTop: '1px solid var(--border)' }}>
                <input
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.nativeEvent.isComposing || e.key === 'Process') return; if (e.key === 'Enter') send(); }}
                  placeholder={t('support.input_ph')}
                  className="flex-1 px-3 py-2 rounded-xl text-xs outline-none"
                  style={{ backgroundColor: 'var(--bg-hover)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
                />
                <button onClick={send} disabled={!input.trim() || sending}
                  className="w-8 h-8 rounded-xl flex items-center justify-center disabled:opacity-40"
                  style={{ backgroundColor: 'var(--brand)', color: '#fff' }}>
                  <Send size={14} />
                </button>
              </div>
            </>
          )}

          {tab === 'ticket' && (
            <div className="p-4 space-y-3">
              {ticketDone ? (
                <p className="text-xs text-center py-8" style={{ color: 'var(--text-secondary)' }}>{t('support.ticket_done')}</p>
              ) : (
                <>
                  <input
                    value={subject}
                    onChange={e => setSubject(e.target.value)}
                    placeholder={t('support.subject_ph')}
                    maxLength={120}
                    className="w-full px-3 py-2 rounded-xl text-xs outline-none"
                    style={{ backgroundColor: 'var(--bg-hover)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
                  />
                  <textarea
                    value={ticketMsg}
                    onChange={e => setTicketMsg(e.target.value)}
                    placeholder={t('support.message_ph')}
                    rows={5}
                    maxLength={4000}
                    className="w-full px-3 py-2 rounded-xl text-xs outline-none resize-none"
                    style={{ backgroundColor: 'var(--bg-hover)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
                  />
                  <button onClick={submitTicket} disabled={subject.trim().length < 4 || ticketMsg.trim().length < 10 || ticketBusy}
                    className="w-full py-2 rounded-xl text-xs font-medium text-white disabled:opacity-40 flex items-center justify-center gap-1.5"
                    style={{ backgroundColor: 'var(--brand)' }}>
                    {ticketBusy ? <Loader2 size={12} className="animate-spin" /> : null}
                    {t('support.submit')}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
